import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import ScrollToTop from "@/components/ScrollToTop";

// Public Pages
import LandingPage from "@/pages/public/LandingPage";
import PricingPage from "@/pages/public/PricingPage";
import AboutPage from "@/pages/public/AboutPage";
import AboutFounderPage from "@/pages/public/AboutFounderPage";
import FAQPage from "@/pages/public/FAQPage";
import ContactPage from "@/pages/public/ContactPage";
import LeadCapturePage from "@/pages/public/LeadCapturePage";
import QuestionnairePage from "@/pages/public/QuestionnairePage";
import EvaluationPage from "@/pages/public/EvaluationPage";
import SchedulePage from "@/pages/public/SchedulePage";
import CheckoutSuccessPage from "@/pages/public/CheckoutSuccessPage";
import ShareReportPage from "@/pages/public/ShareReportPage";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";

// User Portal
import UserPortal from "@/pages/UserPortal";

// Admin Pages
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminClients from "@/pages/admin/AdminClients";
import AdminAppointments from "@/pages/admin/AdminAppointments";
import AdminEngagements from "@/pages/admin/AdminEngagements";
import AdminEngagementDetail from "@/pages/admin/AdminEngagementDetail";
import AdminBilling from "@/pages/admin/AdminBilling";
import AdminAvailability from "@/pages/admin/AdminAvailability";
import AdminPricing from "@/pages/admin/AdminPricing";
import AdminActivity from "@/pages/admin/AdminActivity";
import AdminEbookMetrics from "@/pages/admin/AdminEbookMetrics";

// Client Pages
import ClientPortal from "@/pages/client/ClientPortal";
import OnboardingFlow from "@/pages/client/OnboardingFlow";

// Layout Components
import AdminLayout from "@/components/layouts/AdminLayout";
import ClientLayout from "@/components/layouts/ClientLayout";
import ProtectedRoute from "@/components/ProtectedRoute";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/about-founder" element={<AboutFounderPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/get-started" element={<LeadCapturePage />} />
          <Route path="/questionnaire/:leadId" element={<QuestionnairePage />} />
          <Route path="/evaluation/:leadId" element={<EvaluationPage />} />
          <Route path="/schedule/:leadId" element={<SchedulePage />} />
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
          <Route path="/share/:token" element={<ShareReportPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Onboarding Flow (Protected) */}
          <Route path="/onboarding" element={
            <ProtectedRoute role="user">
              <OnboardingFlow />
            </ProtectedRoute>
          } />

          {/* User Portal Routes (Registered Users) */}
          <Route path="/portal" element={
            <ProtectedRoute role="user">
              <UserPortal />
            </ProtectedRoute>
          } />
          
          {/* Authenticated versions of informational pages */}
          <Route path="/portal/pricing" element={
            <ProtectedRoute role="user">
              <PricingPage />
            </ProtectedRoute>
          } />
          <Route path="/portal/about" element={
            <ProtectedRoute role="user">
              <AboutPage />
            </ProtectedRoute>
          } />
          <Route path="/portal/faq" element={
            <ProtectedRoute role="user">
              <FAQPage />
            </ProtectedRoute>
          } />
          <Route path="/portal/contact" element={
            <ProtectedRoute role="user">
              <ContactPage />
            </ProtectedRoute>
          } />

          {/* Admin Routes */}
          <Route path="/admin" element={
            <ProtectedRoute role="admin">
              <AdminLayout />
            </ProtectedRoute>
          }>
            <Route index element={<AdminDashboard />} />
            <Route path="leads" element={<AdminLeads />} />
            <Route path="clients" element={<AdminClients />} />
            <Route path="appointments" element={<AdminAppointments />} />
            <Route path="engagements" element={<AdminEngagements />} />
            <Route path="engagements/:serId" element={<AdminEngagementDetail />} />
            <Route path="billing" element={<AdminBilling />} />
            <Route path="pricing" element={<AdminPricing />} />
            <Route path="availability" element={<AdminAvailability />} />
            <Route path="activity" element={<AdminActivity />} />
            <Route path="ebook-metrics" element={<AdminEbookMetrics />} />
          </Route>

          {/* Client Routes (Paid Clients) */}
          <Route path="/client" element={
            <ProtectedRoute role="client">
              <ClientLayout />
            </ProtectedRoute>
          }>
            <Route index element={<ClientPortal />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
